import type { EvaluationId, NotFoundError, ProjectId, RepositoryError, SignalId, SqlClient } from "@domain/shared"
import { Context, type Effect } from "effect"
import { z } from "zod"
import type { Evaluation } from "../entities/evaluation.ts"

export const evaluationListLifecycleSchema = z.enum(["active", "archived", "all"])
export type EvaluationListLifecycle = z.infer<typeof evaluationListLifecycleSchema>

export interface EvaluationListOptions {
  readonly limit?: number
  readonly offset?: number
  readonly lifecycle?: EvaluationListLifecycle
}

export interface EvaluationListPage {
  readonly items: readonly Evaluation[]
  readonly hasMore: boolean
  readonly limit: number
  readonly offset: number
}

export interface EvaluationRepositoryShape {
  findById(id: string): Effect.Effect<Evaluation, NotFoundError | RepositoryError, SqlClient>
  save(evaluation: Evaluation): Effect.Effect<void, RepositoryError, SqlClient>
  listByProjectId(input: {
    readonly projectId: ProjectId
    readonly options?: EvaluationListOptions
  }): Effect.Effect<EvaluationListPage, RepositoryError, SqlClient>
  listBySignalId(input: {
    readonly projectId: ProjectId
    readonly signalId: SignalId
    readonly options?: EvaluationListOptions
  }): Effect.Effect<EvaluationListPage, RepositoryError, SqlClient>
  listBySignalIds(input: {
    readonly projectId: ProjectId
    readonly signalIds: readonly SignalId[]
    readonly options?: EvaluationListOptions
  }): Effect.Effect<EvaluationListPage, RepositoryError, SqlClient>
  archive(id: EvaluationId): Effect.Effect<void, RepositoryError, SqlClient>
  unarchive(id: EvaluationId): Effect.Effect<void, RepositoryError, SqlClient>
  softDelete(id: EvaluationId): Effect.Effect<void, RepositoryError, SqlClient>
  softDeleteBySignalId(input: {
    readonly projectId: ProjectId
    readonly signalId: SignalId
  }): Effect.Effect<void, RepositoryError, SqlClient>
}

export class EvaluationRepository extends Context.Service<EvaluationRepository, EvaluationRepositoryShape>()(
  "@domain/evaluations/EvaluationRepository",
) {}
